import { LRUStringCache } from "common/utils/lruStringCache";
import type { AABB2 } from "geom/aabb2Types";
import { aabb3AddPoint, aabb3Init } from "geom/aabb3";
import type { HLRPolygon } from "geom/hlr/hlrTypes";
import { mat4Dot, mat4Invert } from "geom/mat4";
import type { Mat4 } from "geom/mat4";
import { vertexArrayAABB3 } from "geom/mesh";
import type { Mesh3 } from "geom/mesh";
import { planeByThreePoints, planeNormal } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polyLine2ConvexHull } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { polyLine3ConcatAll } from "geom/polyLine3";
import { simplifyMesh } from "gfx/simplifyMesh";
import type { EntityPolyLine } from "lib/house/plans/perspectiveEntities";
import type { LazyPropMesh } from "lib/house/plans/perspectiveTypes";

export interface RenderHLRResult {
  entityPolylines: EntityPolyLine[];
  debugRegions: PolyLine2[];
  degenerateRegions: PolyLine2[];
}

export interface HlrEntityData {
  entityId: string;
  /** BSP draw priority of the source face; used to resolve coplanar overlaps the engine defers. */
  priority: number;
}

function hlrPolygonFromVerts3D(verts3D: number[][], plane: Plane, userData: HlrEntityData): HLRPolygon<HlrEntityData> {
  return { loops3D: [polyLine3ConcatAll(verts3D, true)], plane3D: plane, userData };
}

export interface PropClassificationAabb {
  /** Screen-space 2D bounding box. Drives the overlap test in {@link classifyAndConvertLazyProps}. */
  aabb2D: AABB2;
  /** View-space z-extents, used to order occluders front-to-back; not used for overlap. */
  aabb3D?: { minZ: number; maxZ: number };
}

// For each lazy prop, decide how it should enter the WASM occlusion pass and convert it to
// HLRPolygons. Overlap is tested in screen space (the 2D AABBs) against the building's
// non-prop AABBs:
//   - no overlap  -> nothing can occlude the prop, so emit a cheap silhouette card (its
//     bounding hull) that keeps the annotation alive without exact geometry.
//   - overlaps something -> emit the simplified mesh (one HLRPolygon per triangle) so the
//     engine can clip it precisely.
export async function classifyAndConvertLazyProps(
  lazyPropMeshes: LazyPropMesh[],
  nonPropAabbs: PropClassificationAabb[],
  viewMatrix: Mat4,
): Promise<HLRPolygon<HlrEntityData>[]> {
  const invViewMatrix = mat4Invert(viewMatrix);

  const out: HLRPolygon<HlrEntityData>[] = [];
  const results = await Promise.all(
    lazyPropMeshes.map((lp) => classifyOneProp(lp, nonPropAabbs, viewMatrix, invViewMatrix)),
  );
  for (const batch of results) {
    for (const poly of batch) {
      out.push(poly);
    }
  }
  return out;
}

async function classifyOneProp(
  lazyProp: LazyPropMesh,
  nonPropProjected: PropClassificationAabb[],
  viewMatrix: Mat4,
  invViewMatrix: Mat4,
): Promise<HLRPolygon<HlrEntityData>[]> {
  const worldBbox = vertexArrayAABB3(lazyProp.mesh.mesh.verts);
  if (!worldBbox) {
    return [];
  }

  // Project all 8 AABB corners through the view matrix into a view-space bbox, also collecting each
  // (vx, vy) for the silhouette path.
  const { minX: wx0, minY: wy0, minZ: wz0, maxX: wx1, maxY: wy1, maxZ: wz1 } = worldBbox;
  const viewBbox = aabb3Init();
  const corners2D: [number, number][] = [];
  for (let xi = 0; xi < 2; xi++) {
    for (let yi = 0; yi < 2; yi++) {
      for (let zi = 0; zi < 2; zi++) {
        const [vx, vy, vz] = mat4Dot(viewMatrix, xi ? wx1 : wx0, yi ? wy1 : wy0, zi ? wz1 : wz0);
        corners2D.push([vx, vy]);
        aabb3AddPoint(viewBbox, vx, vy, vz);
      }
    }
  }
  const { minX: propMinX, minY: propMinY, maxX: propMaxX, maxY: propMaxY, maxZ: propMaxZ } = viewBbox;

  const overlapping = nonPropProjected.filter(
    (p) =>
      p.aabb2D.maxX >= propMinX && p.aabb2D.minX <= propMaxX && p.aabb2D.maxY >= propMinY && p.aabb2D.minY <= propMaxY,
  );

  if (overlapping.length === 0) {
    // Nothing overlaps in screen space, so nothing can occlude the prop: emit its silhouette card so
    // the annotation survives.
    const entityId = lazyProp.mesh.propInstanceId ?? lazyProp.mesh.id ?? "";
    return propSilhouetteToHLRPolygon(corners2D, propMaxZ, invViewMatrix, viewMatrix, entityId, lazyProp.priority);
  }

  return simplifyAndConvertProp(lazyProp);
}

// simplifyMesh's output is view-independent, so this cache collapses an elevation set's repeated props
// to one simplify each. Keyed by a hash of the mesh's vertices.
const MAX_SIMPLIFIED_MESH_CACHE = 1024;
const simplifiedMeshCache = new LRUStringCache<Promise<Mesh3>>(MAX_SIMPLIFIED_MESH_CACHE);

function meshSignature(mesh: Mesh3): string {
  const v = mesh.verts;
  // FNV-1a 32-bit hash over the raw float bits. 0x811c9dc5 (the FNV offset basis) is the standard
  // seed and 0x01000193 (the FNV prime) is the per-byte multiplier; both are fixed by the algorithm.
  // Math.imul keeps the multiply in 32-bit integer space; `>>> 0` coerces the final hash to unsigned.
  const u32 = new Uint32Array(v.buffer, v.byteOffset, v.length);
  let h = 0x811c9dc5;
  for (let i = 0; i < u32.length; i += 1) {
    h = Math.imul(h ^ u32[i], 0x01000193);
  }
  return `${v.length}:${h >>> 0}`;
}

// Benchmarks call this between iterations so each run re-simplifies from scratch; production keeps the
// cache warm across an elevation set's views.
export function clearSimplifiedMeshCache(): void {
  simplifiedMeshCache.clear();
}

async function cachedSimplifiedMesh(mesh: Mesh3): Promise<Mesh3> {
  const key = meshSignature(mesh);
  const cached = simplifiedMeshCache.get(key);
  if (cached !== undefined) {
    return cached;
  }
  // Cache the promise (not just the resolved value) so concurrent identical props share one simplify.
  // Evict on failure so a rejected promise doesn't poison this key (a later call retries), but only if
  // the entry is still ours in case a colliding mesh overwrote it.
  const promise = simplifyMesh({ mesh })
    .then((r) => r.mesh)
    .catch((err) => {
      if (simplifiedMeshCache.get(key) === promise) {
        simplifiedMeshCache.delete(key);
      }
      return Promise.reject(err);
    });
  simplifiedMeshCache.add(key, promise);
  return promise;
}

// One HLRPolygon per triangle, bypassing PolygonBSP since HLR needs only geometry and entity metadata.
async function simplifyAndConvertProp(lazyProp: LazyPropMesh): Promise<HLRPolygon<HlrEntityData>[]> {
  const { verts, tris } = await cachedSimplifiedMesh(lazyProp.mesh.mesh);
  const entityId = lazyProp.mesh.propInstanceId ?? lazyProp.mesh.id ?? "";
  const userData: HlrEntityData = { entityId, priority: lazyProp.priority };
  const out: HLRPolygon<HlrEntityData>[] = [];
  for (let i = 0; i + 2 < tris.length; i += 3) {
    const i0 = tris[i] * 3;
    const i1 = tris[i + 1] * 3;
    const i2 = tris[i + 2] * 3;
    const v0 = [verts[i0], verts[i0 + 1], verts[i0 + 2]];
    const v1 = [verts[i1], verts[i1 + 1], verts[i1 + 2]];
    const v2 = [verts[i2], verts[i2 + 1], verts[i2 + 2]];
    const plane = planeByThreePoints(v0[0], v0[1], v0[2], v1[0], v1[1], v1[2], v2[0], v2[1], v2[2]);
    if (!plane) {
      continue;
    }
    out.push(hlrPolygonFromVerts3D([v0, v1, v2], plane, userData));
  }
  return out;
}

// silhouette of the polygon bounding box. Only used for props that overlap no building geometry,
// where over-occluding empty space hides nothing; overlapping props take the exact mesh path instead.
function propSilhouetteToHLRPolygon(
  corners2D: ReadonlyArray<[number, number]>,
  propMaxZ: number,
  invViewMatrix: Mat4,
  viewMatrix: Mat4,
  entityId: string,
  priority: number,
): HLRPolygon<HlrEntityData>[] {
  const flatCorners: number[] = [];
  for (const [x, y] of corners2D) {
    flatCorners.push(x, y);
  }
  const hull = polyLine2ConvexHull(polyLine2New(flatCorners));
  const n = hull.length / 2;
  if (n < 3) {
    return [];
  }

  const worldVerts: number[][] = [];
  for (let i = 0; i < n; i++) {
    const vx = hull[i * 2];
    const vy = hull[i * 2 + 1];
    const [wx, wy, wz] = mat4Dot(invViewMatrix, vx, vy, propMaxZ);
    worldVerts.push([wx, wy, wz]);
  }

  let plane = planeByThreePoints(
    worldVerts[0][0],
    worldVerts[0][1],
    worldVerts[0][2],
    worldVerts[1][0],
    worldVerts[1][1],
    worldVerts[1][2],
    worldVerts[2][0],
    worldVerts[2][1],
    worldVerts[2][2],
  );
  if (!plane) {
    return [];
  }
  // Hull winding needs to be normalized after back-projection; reverse it if it is back-facing.
  const [nx, ny, nz] = planeNormal(plane);
  if (nx * viewMatrix[2] + ny * viewMatrix[6] + nz * viewMatrix[10] < 0) {
    worldVerts.reverse();
    plane = planeByThreePoints(
      worldVerts[0][0],
      worldVerts[0][1],
      worldVerts[0][2],
      worldVerts[1][0],
      worldVerts[1][1],
      worldVerts[1][2],
      worldVerts[2][0],
      worldVerts[2][1],
      worldVerts[2][2],
    );
    if (!plane) {
      return [];
    }
  }

  const userData: HlrEntityData = { entityId, priority };
  return [hlrPolygonFromVerts3D(worldVerts, plane, userData)];
}
